import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  MapPin,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ServiceSite {
  id: number;
  agency_id: number;
  name: string;
  is_active: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SitesList() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ServiceSite | null>(null);
  const [formName, setFormName] = useState("");

  const { data: sites, isLoading } = useQuery<ServiceSite[]>({
    queryKey: ["admin-sites"],
    queryFn: async () => {
      const res = await api.get<{ data: ServiceSite[] }>(
        "/admin/programs/sites/list",
      );
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      api.post("/admin/programs/sites", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sites"] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      name?: string;
      isActive?: boolean;
    }) => api.put(`/admin/programs/sites/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sites"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/programs/sites/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-sites"] });
    },
  });

  function resetForm() {
    setShowForm(false);
    setEditing(null);
    setFormName("");
  }

  function handleEdit(site: ServiceSite) {
    setEditing(site);
    setFormName(site.name);
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editing) {
      updateMutation.mutate({ id: editing.id, name: formName });
    } else {
      createMutation.mutate({ name: formName });
    }
  }

  function toggleActive(site: ServiceSite) {
    updateMutation.mutate({
      id: site.id,
      isActive: !site.is_active,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Service Sites</h1>
          <p className="text-sm text-muted-foreground">
            Manage physical and virtual service delivery locations
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <Plus className="h-4 w-4" />
          New Site
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editing ? "Edit Site" : "New Service Site"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="max-w-md">
                <Input
                  label="Site Name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  placeholder="e.g. Main Office, Community Center"
                />
              </div>

              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {((createMutation.error ?? updateMutation.error) as Error)
                    ?.message ?? "An error occurred"}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="submit"
                  loading={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editing ? "Save Changes" : "Add Site"}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {/* Empty */}
      {!isLoading && (sites ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <MapPin className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No service sites configured yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sites table */}
      {!isLoading && (sites ?? []).length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Site Name
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(sites ?? []).map((site) => (
                  <tr
                    key={site.id}
                    className={cn(
                      "border-b last:border-0 hover:bg-muted/30",
                      !site.is_active && "opacity-60",
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{site.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleActive(site)}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                          site.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600",
                        )}
                      >
                        {site.is_active ? (
                          <>
                            <CheckCircle className="h-3 w-3" /> Active
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3" /> Inactive
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(site)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (
                              confirm(
                                "Delete this site? This cannot be undone.",
                              )
                            ) {
                              deleteMutation.mutate(site.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
